import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useApi } from "@/lib/api";
import {
    AdminSourcesSection,
    AdminDirectoryListingsSection,
    AdminPlacementsSection,
    AdminCreatorPanel,
} from "@/pages/Profile";
import CreatorOverview from "@/pages/admin/CreatorOverview";
import CreatorLanesPanel from "@/pages/admin/CreatorLanesPanel";
import TrackedSearchesTab from "@/pages/admin/TrackedSearchesTab";
import CreatorGuidesTab from "@/pages/admin/CreatorGuidesTab";

/**
 * Admin creator detail. Header with actions (view as, live page, rebuild,
 * delete) plus a tab router. Every tab mounts a real component.
 */

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "profile", label: "Profile" },
    { key: "sources", label: "Sources" },
    { key: "directory", label: "Directory listings" },
    { key: "placements", label: "Placements" },
    { key: "tracked", label: "Tracked searches" },
    { key: "guides", label: "Guides" },
];

function StatusChip({ value }) {
    const map = {
        named: { label: "Named", cls: "bg-signal/15 text-signal" },
        not_yet_named: {
            label: "Not yet named",
            cls: "bg-black/5 text-black/70",
        },
        no_run_yet: {
            label: "No run yet",
            cls: "bg-black/5 text-black/50",
        },
    };
    const m = map[value] || map.no_run_yet;
    return (
        <span
            data-testid="creator-detail-name-status"
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider ${m.cls}`}
        >
            {m.label}
        </span>
    );
}

export default function AdminCreatorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { request } = useApi();
    const [creator, setCreator] = useState(null);
    const [nameStatus, setNameStatus] = useState("no_run_yet");
    const [loading, setLoading] = useState(true);
    // The tab lives in the URL (?tab=placements) so a refresh keeps it.
    const [params, setParams] = useSearchParams();
    const activeTab = params.get("tab") || "overview";
    const setActiveTab = (k) =>
        setParams(k === "overview" ? {} : { tab: k }, { replace: true });
    const [rebuilding, setRebuilding] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request(`/admin/creators/${id}`);
            setCreator(res?.creator || null);
        } catch (err) {
            toast.error(err?.message || "Could not load creator.");
            setCreator(null);
        } finally {
            setLoading(false);
        }
    }, [id, request]);

    useEffect(() => {
        load();
        // Also fetch the name_status from the list payload (one row only).
        (async () => {
            try {
                const res = await request(`/admin/creators?include_internal=1&include_archived=1`);
                const row = (res?.creators || []).find((c) => c.id === id);
                if (row?.name_status) setNameStatus(row.name_status);
            } catch {
                // noop
            }
        })();
    }, [id, request, load]);

    const publicPageHref = useMemo(() => {
        if (!creator?.samoony_handle) return null;
        return `/@${creator.samoony_handle}`;
    }, [creator]);

    const rebuild = useCallback(async () => {
        if (!creator?.id) return;
        if (
            !window.confirm(
                `Trigger a rebuild for /@${creator.samoony_handle}?`,
            )
        ) {
            return;
        }
        setRebuilding(true);
        try {
            await request(`/admin/rebuild/${creator.id}`, { method: "POST" });
            toast.success("Rebuild queued.");
        } catch (err) {
            toast.error(err?.message || "Could not queue rebuild.");
        } finally {
            setRebuilding(false);
        }
    }, [creator, request]);

    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteText, setDeleteText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const deleteMatches =
        !!creator?.samoony_handle &&
        deleteText.trim().toLowerCase() === creator.samoony_handle.toLowerCase();

    const remove = useCallback(async () => {
        if (!creator?.id || !deleteMatches || deleting) return;
        setDeleting(true);
        try {
            await request(`/admin/creators/${creator.id}`, { method: "DELETE" });
            toast.success(`Deleted /@${creator.samoony_handle}.`);
            navigate("/admin/creators", { replace: true });
        } catch (err) {
            toast.error(err?.message || "Could not delete.");
            setDeleting(false);
        }
    }, [creator, deleteMatches, deleting, request, navigate]);

    if (loading) {
        return (
            <div className="text-sm text-black/50">Loading creator...</div>
        );
    }
    if (!creator) {
        return (
            <div
                data-testid="creator-detail-missing"
                className="text-sm text-black/60"
            >
                Creator not found.{" "}
                <Link to="/admin/creators" className="underline">
                    Back to creators
                </Link>
            </div>
        );
    }

    return (
        <article>
            <nav className="mb-3 text-xs font-mono text-black/50">
                <Link to="/admin/creators" className="underline">
                    Creators
                </Link>
                <span> › </span>
                <span>/@{creator.samoony_handle}</span>
            </nav>

            <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
                <div className="min-w-0">
                    <h1
                        data-testid="creator-detail-name"
                        className="font-display text-3xl font-bold tracking-tight"
                    >
                        {creator.display_name ||
                            creator.first_name ||
                            `/@${creator.samoony_handle}`}
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                        <span className="font-mono text-sm text-black/70">
                            /@{creator.samoony_handle}
                        </span>
                        <StatusChip value={nameStatus} />
                        {creator.is_internal && (
                            <span className="text-[11px] font-mono uppercase tracking-wider text-electric">
                                Internal
                            </span>
                        )}
                        {creator.archived_at && (
                            <span className="text-[11px] font-mono uppercase tracking-wider text-black/40">
                                Archived
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        data-testid="creator-detail-view-as"
                        onClick={() =>
                            navigate(`/admin/creators/${creator.id}/view`)
                        }
                        className="samoony-chip"
                    >
                        View as creator
                    </button>
                    {publicPageHref && creator.approved_at && (
                        <a
                            data-testid="creator-detail-live-page"
                            href={publicPageHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="samoony-chip"
                        >
                            Live page ↗
                        </a>
                    )}
                    <button
                        data-testid="creator-detail-rebuild"
                        onClick={rebuild}
                        disabled={rebuilding || !creator.approved_at}
                        className="samoony-chip disabled:opacity-30"
                    >
                        {rebuilding ? "Rebuilding..." : "Rebuild"}
                    </button>
                    <button
                        data-testid="creator-detail-delete"
                        onClick={() => {
                            setConfirmingDelete((v) => !v);
                            setDeleteText("");
                        }}
                        className="samoony-chip text-signal"
                    >
                        Delete
                    </button>
                </div>
            </header>

            {confirmingDelete && (
                <section
                    data-testid="creator-detail-delete-confirm"
                    className="mb-5 bg-white border border-signal/40 rounded-2xl p-5"
                >
                    <div className="samoony-label mb-2 text-signal">Delete creator</div>
                    <p className="text-sm text-ink mb-3">
                        This permanently removes /@{creator.samoony_handle} and
                        everything attached: page drafts, reads, build history,
                        tracked searches, placements and update notices. The
                        public page will stop working. This cannot be undone.
                    </p>
                    <label className="block text-xs font-mono text-black/60 mb-1">
                        Type {creator.samoony_handle} to confirm
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            data-testid="creator-detail-delete-input"
                            value={deleteText}
                            onChange={(e) => setDeleteText(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                            className="h-10 flex-1 min-w-[200px] rounded-lg border border-black/15 px-3 text-sm font-mono"
                        />
                        <button
                            data-testid="creator-detail-delete-confirm-button"
                            onClick={remove}
                            disabled={!deleteMatches || deleting}
                            className="h-10 px-4 rounded-full bg-signal text-white text-sm font-medium disabled:opacity-30"
                        >
                            {deleting ? "Deleting..." : "Delete permanently"}
                        </button>
                        <button
                            data-testid="creator-detail-delete-cancel"
                            onClick={() => setConfirmingDelete(false)}
                            disabled={deleting}
                            className="samoony-chip"
                        >
                            Cancel
                        </button>
                    </div>
                </section>
            )}

            <div
                role="tablist"
                aria-label="Creator sections"
                data-testid="creator-detail-tabs"
                className="flex items-center gap-1 flex-nowrap border-b border-black/10 mb-5 overflow-x-auto no-scrollbar"
            >
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        role="tab"
                        aria-selected={activeTab === t.key}
                        data-testid={`creator-detail-tab-${t.key}`}
                        onClick={() => setActiveTab(t.key)}
                        className={[
                            "px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px",
                            activeTab === t.key
                                ? "border-ink text-ink font-medium"
                                : "border-transparent text-black/60 hover:text-ink",
                        ].join(" ")}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div data-testid={`creator-detail-panel-${activeTab}`}>
                {activeTab === "overview" && (
                    <>
                        <CreatorLanesPanel
                            key={`${creator.id}-${creator.updated_at || ""}`}
                            creator={creator}
                            onSaved={load}
                        />
                        <CreatorOverview
                            creator={creator}
                            onJumpTab={(k) => setActiveTab(k)}
                        />
                        <details className="mt-4 bg-white border border-black/10 rounded-2xl">
                            <summary
                                data-testid="creator-detail-legacy-admin-toggle"
                                className="list-none cursor-pointer px-5 py-4 flex items-center justify-between"
                            >
                                <span className="samoony-label">
                                    More admin tools (legacy)
                                </span>
                                <span className="text-xs text-black/40">
                                    Rename, Release, Approve ownership, Tracked
                                    searches, Leader lookup, Download chunks,
                                    Dedupe places. Promoted into tabs over the
                                    next slices.
                                </span>
                            </summary>
                            <div className="px-2 pb-2">
                                <AdminCreatorPanel
                                    creator={creator}
                                    publicPageHref={publicPageHref}
                                />
                            </div>
                        </details>
                    </>
                )}
                {activeTab === "profile" && (
                    <ProfileTab creator={creator} />
                )}
                {activeTab === "sources" && (
                    <AdminSourcesSection creator={creator} />
                )}
                {activeTab === "directory" && (
                    <AdminDirectoryListingsSection creator={creator} />
                )}
                {activeTab === "placements" && (
                    <AdminPlacementsSection creator={creator} />
                )}
                {activeTab === "tracked" && (
                    <TrackedSearchesTab creator={creator} />
                )}
                {activeTab === "guides" && (
                    <CreatorGuidesTab creator={creator} />
                )}
            </div>
        </article>
    );
}

function Placeholder({ title, body }) {
    return (
        <div
            data-testid="creator-detail-placeholder"
            className="bg-white border border-black/10 rounded-2xl p-5"
        >
            <div className="samoony-label mb-2">{title}</div>
            <p className="text-sm text-black/60">{body}</p>
        </div>
    );
}

function ProfileTab({ creator }) {
    // Read-only read of the stored fields. No pronouns are shown (point 9).
    const rows = [
        ["Samoony handle", `/@${creator.samoony_handle}`],
        ["Instagram handle", creator.handles?.instagram || "—"],
        ["TikTok handle", creator.handles?.tiktok || "—"],
        ["YouTube handle", creator.handles?.youtube || "—"],
        ["LinkedIn profile", creator.handles?.linkedin || "—"],
        ["Display name", creator.display_name || "—"],
        ["First name", creator.first_name || "—"],
        ["Primary niche", creator.primary_niche || "—"],
        ["City", creator.city || "—"],
        ["Region", creator.region || "—"],
        ["Approved at", creator.approved_at || "—"],
        ["Ownership status", creator.ownership_status || "—"],
    ];
    return (
        <div className="bg-white border border-black/10 rounded-2xl p-5">
            <div className="samoony-label mb-3">Profile</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {rows.map(([k, v]) => (
                    <div
                        key={k}
                        data-testid={`profile-tab-row-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="flex items-start justify-between gap-3 border-b border-black/5 py-1.5"
                    >
                        <dt className="text-black/50 text-xs font-mono uppercase tracking-wider">
                            {k}
                        </dt>
                        <dd className="text-ink text-right break-all">
                            {v}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}
